/**
 * /memories/:id — single-photo detail view.
 *
 * The image area is a persistent carousel: swipes advance the strip
 * without remounting this page. URL is kept in sync via
 * `history.replaceState`, so a deep-link / share-URL / refresh always
 * lands on the photo currently in view, and the browser back button
 * still exits to the grid (we never push, only replace).
 *
 * Caption / likes / comments below are driven by `currentId` (local
 * state hoisted out of the carousel), so they swap as the user
 * swipes — but the route stays mounted and the page scroll position
 * is preserved.
 */
import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { MentionTextarea, renderWithMentions } from '../components/MentionTextarea';
import { PhotoCarousel } from '../components/PhotoCarousel';
import { IconButton } from '../components/IconButton';
import { ConfirmDialog } from '../components/ConfirmDialog';
import {
  IconArrowLeft,
  IconComment,
  IconHeart,
  IconPencil,
  IconTrash,
} from '../components/icons';
import { useMentionRoster } from '../lib/circles';

interface PhotoDetail {
  id: string;
  caption: string | null;
  uploadedBy: string;
  takenAt: number | null;
  largeUrl: string;
  likes: number;
  likedByMe: boolean;
  comments: number;
}

interface Comment {
  id: string;
  authorId: string;
  authorName: string;
  body: string;
  createdAt: number;
}

export default function PhotoDetailPage() {
  const { id: routeId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();
  const qc = useQueryClient();
  const me = auth.state === 'signed-in' ? auth.me : null;

  // URL-driven context: `year` narrows family swipe; `circleId` switches
  // the whole detail (siblings, mention roster, route-back) to a single
  // Generations circle. Both are preserved as the carousel advances.
  const searchParams = new URLSearchParams(location.search);
  const filterYear = searchParams.get('year');
  const circleId = searchParams.get('circleId');
  // When present, swipe is scoped to this memory's ordered photos
  // instead of the family/year timeline.
  const memoryId = searchParams.get('memoryId');

  // Source of truth for "which photo is on screen." Initialized from
  // the route param, then driven by the carousel via `onChange`. The
  // route param itself is only read once (on mount) because we sync
  // URL changes through `history.replaceState`, which React Router
  // doesn't observe — that's exactly what we want.
  const [currentId, setCurrentId] = useState<string>(routeId ?? '');

  // Keep the address bar in sync with the visible photo, without going
  // through React Router (which would re-trigger route effects). Build
  // the query string with URLSearchParams so adding/removing params
  // never drops one when carrying the other.
  useEffect(() => {
    if (!currentId || currentId === routeId) return;
    const qs = new URLSearchParams();
    if (filterYear) qs.set('year', filterYear);
    if (circleId) qs.set('circleId', circleId);
    if (memoryId) qs.set('memoryId', memoryId);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    window.history.replaceState(null, '', `/memories/${currentId}${suffix}`);
  }, [currentId, routeId, filterYear, circleId, memoryId]);

  // Browser back/forward across history entries that point at
  // different photos (rare since we only replaceState, but possible
  // if the user deep-links). Re-sync local state from the URL.
  useEffect(() => {
    function onPop() {
      const m = window.location.pathname.match(/^\/memories\/([^/?]+)/);
      if (m && m[1] && m[1] !== currentId) setCurrentId(m[1]);
    }
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [currentId]);

  // Cross-household roster, fetched only when we're in a circle context.
  // useMentionRoster returns undefined when circleId is null, which makes
  // MentionTextarea fall back to its useAuth() household default.
  const mentionRoster = useMentionRoster(circleId);

  // Mirror the carousel's siblings query so we can detect a stale
  // circle deep-link: photo loaded (caller can still see it via some
  // other circle / family scope) but ?circleId=X points at a circle
  // they no longer belong to → siblings 403, swipe quietly fails.
  // React Query dedupes by key, so this is just an extra subscriber
  // on the same cache entry; no additional network request.
  const siblingsForBanner = useQuery({
    queryKey: [
      'photo-siblings',
      currentId,
      filterYear,
      circleId ?? null,
      memoryId ?? null,
    ],
    queryFn: () => {
      const qs = new URLSearchParams();
      if (filterYear) qs.set('year', filterYear);
      if (circleId) qs.set('circleId', circleId);
      if (memoryId) qs.set('memoryId', memoryId);
      const suffix = qs.toString() ? `?${qs.toString()}` : '';
      return api.get(`/photos/${currentId}/siblings${suffix}`);
    },
    enabled: !!currentId,
    staleTime: 5 * 60_000,
  });
  const circleAccessRevoked =
    !!circleId &&
    siblingsForBanner.error instanceof ApiError &&
    siblingsForBanner.error.status === 403;

  const photoQ = useQuery({
    queryKey: ['photo', currentId],
    queryFn: () => api.get<PhotoDetail>(`/photos/${currentId}`),
    enabled: !!currentId,
    staleTime: 5 * 60_000,
    placeholderData: (prev) => prev,
  });
  const commentsQ = useQuery({
    queryKey: ['photo-comments', currentId],
    queryFn: () => api.get<{ comments: Comment[] }>(`/photos/${currentId}/comments`),
    enabled: !!currentId,
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });

  const toggleLike = useMutation({
    mutationFn: (liked: boolean) =>
      liked
        ? api.delete(`/photos/${currentId}/like`)
        : api.post(`/photos/${currentId}/like`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['photo', currentId] }),
  });

  const updateCaption = useMutation({
    mutationFn: (args: { caption: string | null; mentions: string[] }) =>
      api.patch(`/photos/${currentId}`, {
        ...args,
        ...(circleId ? { circleId } : {}),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['photo', currentId] }),
  });

  const addComment = useMutation({
    mutationFn: (args: { body: string; mentions: string[] }) =>
      api.post<{ comment: Comment }>(`/photos/${currentId}/comments`, {
        ...args,
        ...(circleId ? { circleId } : {}),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['photo-comments', currentId] });
      void qc.invalidateQueries({ queryKey: ['photo', currentId] });
    },
  });

  const [commentDraft, setCommentDraft] = useState('');
  const [commentMentions, setCommentMentions] = useState<string[]>([]);
  const [editingCaption, setEditingCaption] = useState(false);
  const [captionDraft, setCaptionDraft] = useState('');
  const [captionMentions, setCaptionMentions] = useState<string[]>([]);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Caption editor closes whenever the carousel advances to a new
  // photo — otherwise edits from photo A leak into photo B's form.
  useEffect(() => {
    setEditingCaption(false);
    setCommentDraft('');
    setCommentMentions([]);
    setConfirmDelete(false);
  }, [currentId]);

  const deletePhoto = useMutation({
    mutationFn: () => api.delete(`/photos/${currentId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['photo-grid'] });
      navigate('/memories');
    },
  });

  if (photoQ.isLoading && !photoQ.data) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-soft text-sm">Loading…</div>
      </div>
    );
  }
  if (photoQ.error || !photoQ.data) {
    return (
      <div className="px-6 pt-12 text-center">
        <p className="text-soft">Couldn't load this photo.</p>
        <Link to="/memories" className="block text-oxblood underline mt-4">
          ← back to Memories
        </Link>
      </div>
    );
  }

  const photo = photoQ.data;
  const canEditCaption = me?.member.id === photo.uploadedBy;
  const canDelete = canEditCaption;

  return (
    <div className="px-5 pt-3 pb-6">
      <div className="flex items-center justify-between mb-3 -mx-2">
        <IconButton
          icon={<IconArrowLeft />}
          label="Back"
          onClick={() => {
            // Return to wherever the user came from — Memories grid,
            // Generations circle, an alerts deep-link, etc. Falls back
            // to the year-filtered Memories grid on cold deep-links
            // (no history) so we don't strand the user.
            if (window.history.length > 1) {
              navigate(-1);
            } else {
              navigate(filterYear ? `/memories?year=${filterYear}` : '/memories');
            }
          }}
        />
        {canDelete && (
          <IconButton
            icon={<IconTrash />}
            label="Delete photo"
            variant="destructive"
            onClick={() => setConfirmDelete(true)}
          />
        )}
      </div>

      {circleAccessRevoked && (
        <div className="mb-3 px-3 py-2 rounded-lg bg-oxblood/10 text-oxblood text-sm">
          You're no longer a member of the circle this link was shared from. The photo's
          still visible to you, but swipe isn't available — open{' '}
          <Link to="/generations" className="underline">
            Generations
          </Link>{' '}
          to browse a circle you do belong to.
        </div>
      )}
      <article className="card overflow-hidden mb-5">
        <PhotoCarousel
          centerId={currentId}
          filterYear={filterYear}
          circleId={circleId}
          memoryId={memoryId}
          alt={photo.caption ?? ''}
          onChange={setCurrentId}
        />
        <div className="p-5">
          {photo.takenAt && (
            <div className="kicker text-oxblood">
              {new Date(photo.takenAt).toLocaleDateString([], {
                month: 'long',
                day: 'numeric',
                year: 'numeric',
              })}
            </div>
          )}

          {editingCaption ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                updateCaption.mutate({
                  caption: captionDraft.trim() || null,
                  mentions: captionMentions,
                });
                setEditingCaption(false);
              }}
              className="mt-3"
            >
              <MentionTextarea
                value={captionDraft}
                onChange={(v, m) => {
                  setCaptionDraft(v);
                  setCaptionMentions(m);
                }}
                members={mentionRoster}
                rows={3}
                placeholder="What is this moment? Add a few words."
                className="w-full bg-paper border border-line rounded-lg px-3 py-2 focus:outline-none focus:border-oxblood text-ink"
              />
              <div className="flex gap-2 mt-2 justify-end">
                <button
                  type="button"
                  onClick={() => setEditingCaption(false)}
                  className="text-soft text-sm px-3 py-1"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-oxblood text-paper text-sm font-medium px-4 py-1.5 rounded-full"
                >
                  Save
                </button>
              </div>
            </form>
          ) : (
            <div className="flex items-start gap-2 mt-2">
              <h2
                className={`flex-1 font-serif italic text-2xl ${
                  photo.caption ? 'text-ink' : 'text-muted'
                } ${canEditCaption ? 'cursor-pointer' : ''}`}
                onClick={
                  canEditCaption
                    ? () => {
                        setCaptionDraft(photo.caption ?? '');
                        setCaptionMentions([]);
                        setEditingCaption(true);
                      }
                    : undefined
                }
              >
                {photo.caption
                  ? renderWithMentions(photo.caption)
                  : canEditCaption
                    ? 'Add a caption…'
                    : 'No caption'}
              </h2>
              {canEditCaption && (
                <IconButton
                  icon={<IconPencil />}
                  label="Edit caption"
                  onClick={() => {
                    setCaptionDraft(photo.caption ?? '');
                    setCaptionMentions([]);
                    setEditingCaption(true);
                  }}
                />
              )}
            </div>
          )}

          <div className="flex items-center gap-5 mt-5">
            <button
              onClick={() => toggleLike.mutate(photo.likedByMe)}
              className={`flex items-center gap-2 ${
                photo.likedByMe ? 'text-oxblood' : 'text-soft'
              }`}
              aria-label={photo.likedByMe ? 'Unlike' : 'Like'}
            >
              <IconHeart filled={photo.likedByMe} size={24} />
              <span className="text-sm font-medium">{photo.likes}</span>
            </button>
            <div className="flex items-center gap-2 text-soft">
              <IconComment size={24} />
              <span className="text-sm font-medium">{photo.comments}</span>
            </div>
          </div>
        </div>
      </article>

      <section>
        <h3 className="font-serif text-xl text-ink mb-3">Comments</h3>

        {commentsQ.data && (
          <div className="space-y-3 mb-4">
            {commentsQ.data.comments.length === 0 ? (
              <p className="text-muted italic text-sm py-2">
                Be the first to say something.
              </p>
            ) : (
              commentsQ.data.comments.map((c) => (
                <div key={c.id} id={`c_${c.id}`} className="card p-3">
                  <div className="flex items-center justify-between">
                    <span className="font-serif text-base text-oxblood">
                      {c.authorName}
                    </span>
                    <span className="text-xs text-muted">
                      {new Date(c.createdAt).toLocaleDateString([], {
                        month: 'short',
                        day: 'numeric',
                      })}
                    </span>
                  </div>
                  <p className="text-ink mt-1 whitespace-pre-wrap">
                    {renderWithMentions(c.body)}
                  </p>
                </div>
              ))
            )}
          </div>
        )}

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!commentDraft.trim()) return;
            addComment.mutate({
              body: commentDraft.trim(),
              mentions: commentMentions,
            });
            setCommentDraft('');
            setCommentMentions([]);
          }}
          className="card p-3"
        >
          <MentionTextarea
            value={commentDraft}
            onChange={(v, m) => {
              setCommentDraft(v);
              setCommentMentions(m);
            }}
            members={mentionRoster}
            rows={2}
            placeholder="Add a comment…"
            className="w-full bg-transparent text-ink placeholder:text-muted resize-none focus:outline-none"
          />
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={!commentDraft.trim() || addComment.isPending}
              className="h-9 px-4 rounded-full bg-oxblood text-paper text-sm font-medium disabled:opacity-50"
            >
              {addComment.isPending ? 'Posting…' : 'Post'}
            </button>
          </div>
        </form>
      </section>

      {confirmDelete && (
        <ConfirmDialog
          title="Delete this photo?"
          message="The photo, its caption, comments, and likes will be removed for everyone."
          confirmLabel="Delete"
          destructive
          busy={deletePhoto.isPending}
          error={deletePhoto.isError ? 'Could not delete. Try again.' : null}
          onConfirm={() => deletePhoto.mutate()}
          onCancel={() => setConfirmDelete(false)}
        />
      )}
    </div>
  );
}
